import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Badge,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PatternBlockSelector = ({ selectedBlock, onSelectBlock, brandId }) => {
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [patterns, setPatterns] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPatterns();
  }, [brandId, categoryFilter]);

  const fetchPatterns = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (brandId) {
        params.append('brandId', brandId);
      }
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      // Only fetch published and available patterns
      params.append('status', 'published');
      params.append('available', 'true');

      const response = await fetch(`/api/patterns?${params.toString()}`, {
        headers: authHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load patterns');
      }

      const data = await response.json();
      setPatterns(data.patterns || []);
    } catch (err) {
      console.error('Error fetching patterns:', err);
      setError(err.message);
      toast.error('Failed to load patterns');
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(patterns.map(b => b.category))];

  const filteredBlocks = categoryFilter === 'all'
    ? patterns
    : patterns.filter(b => b.category === categoryFilter);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={fetchPatterns} startIcon={<RefreshIcon />}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Select a Garment Type
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a complete garment to customize. All pattern pieces (front, back, sleeves, etc.) will be generated together and adjusted simultaneously based on your DNA layers and measurements.
          </Typography>
        </Box>
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchPatterns}
        >
          Refresh
        </Button>
      </Box>

      {/* Category Filter */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        {categories.map((cat) => (
          <Chip
            key={cat}
            label={cat === 'all' ? 'All Categories' : cat}
            onClick={() => setCategoryFilter(cat)}
            color={categoryFilter === cat ? 'primary' : 'default'}
            variant={categoryFilter === cat ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Empty State */}
      {filteredBlocks.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            No patterns available in this category. {categoryFilter !== 'all' ? 'Try selecting a different category.' : 'Contact your administrator to add patterns.'}
          </Typography>
        </Alert>
      )}

      {/* Pattern Blocks Grid - Shopping Style */}
      <Grid container spacing={3}>
        {filteredBlocks.map((block) => (
          <Grid item xs={12} sm={6} md={4} key={block._id}>
            <Badge
              badgeContent={selectedBlock?._id === block._id ? <CheckIcon /> : null}
              color="primary"
              sx={{
                width: '100%',
                '& .MuiBadge-badge': {
                  right: 16,
                  top: 16,
                  padding: '8px',
                  borderRadius: '50%',
                },
              }}
            >
              <Card
                sx={{
                  height: '100%',
                  width: '100%',
                  borderWidth: selectedBlock?._id === block._id ? 3 : 1,
                  borderColor: selectedBlock?._id === block._id ? 'primary.main' : 'divider',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  opacity: block.available ? 1 : 0.7,
                  '&:hover': {
                    transform: block.available ? 'translateY(-4px)' : 'none',
                    boxShadow: block.available ? 6 : 1,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => block.available && onSelectBlock(block)}
                  disabled={!block.available}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                >
                  {/* Product Image */}
                  <Box sx={{ position: 'relative', width: '100%', paddingTop: '100%', bgcolor: 'grey.100' }}>
                    {block.imageUrl ? (
                      <CardMedia
                        component="img"
                        image={block.imageUrl}
                        alt={block.name}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '16px',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'grey.200',
                          flexDirection: 'column',
                          gap: 1,
                        }}
                      >
                        <Typography variant="h3" color="text.secondary">
                          {block.name?.charAt(0) || '?'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          No Image
                        </Typography>
                      </Box>
                    )}

                    {/* Status Badges */}
                    <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'flex', gap: 1, flexDirection: 'column' }}>
                      <Chip
                        label={block.category}
                        size="small"
                        sx={{ bgcolor: 'white', fontWeight: 600 }}
                      />
                      {block.comingSoon && (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                      {block.available && (
                        <Chip
                          label="Available"
                          size="small"
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>

                    {/* Piece Count Badge */}
                    <Chip
                      label={`${block.pieceCount} pieces`}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        right: 12,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Product Name */}
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {block.name}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        flexGrow: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {block.description}
                    </Typography>

                    {/* Pattern Pieces */}
                    {block.pieces && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} gutterBottom>
                          Includes:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {block.pieces.map((piece, index) => (
                            <Chip
                              key={index}
                              label={piece}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22 }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Measurements */}
                    {block.measurements && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" fontWeight={600} gutterBottom>
                          Measurements Required: {block.measurements.length}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Badge>
          </Grid>
        ))}
      </Grid>

      {/* Selected Block Alert */}
      {selectedBlock && (
        <Alert severity="success" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>{selectedBlock.name}</strong> selected. Click "Next" to configure DNA layers.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default PatternBlockSelector;
